interface LinearInterpolationCriteria {
    start: number;
    stop: number;
    amount: number;
    // Frame delta
    delta?: number;
    // Target FPS
    fps?: number;
}

interface LeftPadNumberOptions {
    padding?: number;
    prefix?: string;
}

export function assertNumber(value?: unknown): value is number {
    // Both `NaN` and `Infinity` are of type `number`,
    // but will not pass the `isFinite()` check.
    return typeof value === 'number' && isFinite(value);
}

export function abbreviateNumber(value: number, locale = 'en-US') {
    return Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
    }).format(value);
}

export function formatInt(value = 0) {
    return value.toLocaleString('en-US');
}

export function formatFloat(value?: string | number, decimalLength?: number) {
    // NOTE: It is preferred to use the `ethers` dependency
    // for dealing with "big numbers" safely.

    const parsedValue = typeof value === 'string' ? parseFloat(value) : value;
    const safeValue = parsedValue || 0;

    const intPart = Math.trunc(safeValue);

    // Cannot use subtraction (value - intPart), because JS
    // could give us back an "out of range" calculation.
    const decimalPart = `${safeValue}`.split('.')[1] ?? '';
    const formatted = [formatInt(intPart)];

    // We may prefer rounding instead of trimming the decimal length.
    if (decimalPart) formatted.push(decimalPart.slice(0, decimalLength));

    // If the provided `value` exceeds `14` digits,
    // we may end up getting back a rounded value.
    return formatted.join('.');
}

export function formatUsd(value = 0, stripZeroDecimal = false) {
    // If we ever want to support other `fiat` currencies, then we
    // need to accept a `currency` argument.
    const formatted = value.toLocaleString('en-US', {
        currency: 'USD',
        style: 'currency',
    });

    return stripZeroDecimal ? formatted.replace(/\.00?$/, '') : formatted;
}

export function formatCrypto(value = 0, decimalLength = 4, padEnd = false) {
    const formatted = formatFloat(value, decimalLength);

    if (!padEnd) return formatted;

    const formattedParts = formatted.split('.');
    const decimalPart = formattedParts[1] ?? '';

    const paddedParts = [formattedParts[0], decimalPart.padEnd(decimalLength, '0')];

    return paddedParts.join('.');
}

export function trimDecimals(value = 0, decimalLength = 2) {
    // Results in an amount of exactly `decimalLength`.
    return parseFloat(value.toFixed(decimalLength));
}

export function roundToCommonDecimals(value: number) {
    // Rounds to a max of 2 decimals.
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function roundNumber(value: number, decimals = 0) {
    const multiplier = Math.pow(10, decimals);
    return Math.round(value * multiplier) / multiplier;
}

export function clamp(min: number, preference: number, max: number) {
    return Math.min(Math.max(min, preference), max);
}

export function lerp({ start, stop, amount, delta, fps }: LinearInterpolationCriteria) {
    if (typeof delta === 'undefined') {
        return (1 - amount) * start + amount * stop;
    }

    const relativeDelta = delta / (1 / (fps || 60));
    const smoothing = 1 - amount;

    return lerp({
        start,
        stop,
        amount: 1 - Math.pow(smoothing, relativeDelta),
    });
}

export function flipNumberSign(value: number) {
    if (Object.is(value, -0)) return 0;
    return value ? -value : value;
}

export function getProgressPercentage(value: number, max: number) {
    return Math.floor((value / max) * 100);
}

export function percentageBetweenValues(from: number, to: number) {
    return (to / from) * 100 - 100;
}

export function leftPadNumber(value: number, options: LeftPadNumberOptions = {}) {
    const { padding = 2, prefix = '0' } = options;
    return value.toString().padStart(padding, prefix);
}

export function percentToHex(percentage = 0) {
    // Adapted from:
    // https://gist.github.com/lopspower/03fb1cc0ac9f32ef38f4
    const percent = Math.max(0, Math.min(100, percentage));
    const intValue = Math.round((percent / 100) * 255);
    const hexValue = intValue.toString(16);

    return hexValue.padStart(2, '0').toUpperCase();
}
